/** What the live view derives from the staff frame's `channels` block (STORY-124).
 *
 * Pure functions, so the stage, the deck and the roster all read the same rule and a test
 * can pin the rule without rendering. Nothing here takes a student's name: every input and
 * every output is a count or a channel key.
 */
import { CHANNEL_ITEM_TYPES, CHANNEL_MAX_OPTIONS, IMAGE_FREE_CHANNELS, type Channels, type Snapshot } from "./useLive";

/** Whether `channel` can carry an item of this shape — the same rule as
 * `events.channels.supports`: a channel not in the table carries everything (the browser); a
 * listed one carries its listed types, with one to its most options where it has a limit (a
 * printed card: four; a clicker: its two buttons), and an image-free one (telegram) never an
 * item whose options carry an image. A card and a clicker may: the image is on the projector. */
export function channelSupports(channel: string, item: { type: string; options?: { image?: string | null }[] }): boolean {
  const allowed = CHANNEL_ITEM_TYPES[channel];
  if (!allowed) return true;
  if (!allowed.has(item.type)) return false;
  const options = item.options ?? [];
  const most = CHANNEL_MAX_OPTIONS[channel];
  if (most !== undefined && (options.length < 1 || options.length > most)) return false;
  if (IMAGE_FREE_CHANNELS.has(channel)) return !options.some((o) => !!o.image);
  return true;
}

/** The channels the course has respondents on, other than the browser — the ones a marker
 * or a line may be about. An empty list means nothing channel-related is shown at all. */
export function courseChannels(channels: Channels | undefined): string[] {
  if (!channels?.course) return [];
  return Object.entries(channels.course)
    .filter(([chan, n]) => chan !== "web" && n > 0)
    .map(([chan]) => chan)
    .sort();
}

/** Channels among the course's that cannot carry `item`, with how many students are on
 * each — the deck's marker for a card not yet opened. Empty when the course has nobody on a
 * listed channel, so a browser-only class never sees a marker or an empty column. */
export function unsupportedFor(channels: Channels | undefined, item: { type: string; options?: { image?: string | null }[] }): { channel: string; count: number }[] {
  return courseChannels(channels)
    .filter((chan) => !channelSupports(chan, item))
    .map((chan) => ({ channel: chan, count: channels?.course[chan] ?? 0 }));
}

export type ChannelLineKind = "unreachable" | "unreachableNext" | "answering" | "cannotAnswer";
export type ChannelLineItem = { kind: ChannelLineKind; channel: string; count: number | null };

/** The lines the stage states, in order: a channel that is down replaces its answering line
 * while that holds; then who is still answering; then, for the open round, who cannot be
 * reached with this item. `count` is null for a channel whose course count the frame does
 * not carry (a host whose cache is stale): the words then say "students" with no number.
 * With `upcoming` (nothing has been sent on this face: the next question is only selected)
 * the down channel's line looks forward, "will not get the next question", never "may not
 * have this question" about a question that has not left. */
export function channelLines(channels: Channels | undefined, roundOpen: boolean, upcoming = false): ChannelLineItem[] {
  if (!channels) return [];
  const out: ChannelLineItem[] = [];
  const down = new Set<string>();
  for (const [chan, state] of Object.entries(channels.state ?? {})) {
    if (state === "unreachable" || state === "disabled") {
      down.add(chan);
      const n = channels.course?.[chan];
      // a course with nobody on the channel has nobody to warn about; a frame that does
      // not carry the course count states the fact without a number
      if (n !== 0) out.push({ kind: upcoming ? "unreachableNext" : "unreachable", channel: chan, count: n === undefined ? null : n });
    }
  }
  for (const [chan, n] of Object.entries(channels.outstanding ?? {})) {
    if (n > 0 && !down.has(chan)) out.push({ kind: "answering", channel: chan, count: n });
  }
  if (roundOpen)
    for (const [chan, n] of Object.entries(channels.unsupported ?? {})) {
      if (n > 0 && !down.has(chan)) out.push({ kind: "cannotAnswer", channel: chan, count: n });
    }
  return out;
}

/** How many of the current round's answers came by each non-browser channel — what the
 * roster may state in anonymous join mode, where a per-row marker is not allowed. */
export function answeredByChannel(aggregate: Snapshot["aggregate"]): { channel: string; count: number }[] {
  const counts: Record<string, number> = {};
  for (const r of Object.values(aggregate?.per_respondent ?? {})) {
    // a clicker's "I'm lost" chord is not an answer (STORY-086)
    if (r.lost) continue;
    if (r.channel && r.channel !== "web") counts[r.channel] = (counts[r.channel] ?? 0) + 1;
  }
  return Object.entries(counts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([channel, count]) => ({ channel, count }));
}
